import { Box, Spinner } from '@kluein/klue-ui';
import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { CaretDownIcon, CaretUpIcon, ExportIcon } from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chat } from 'modules/shared/libs/chat/components/Chat';
import CitationItem from 'modules/shared/libs/chat/components/CitationItem';
import CopyMessageAction from 'modules/shared/libs/chat/components/CopyMessageAction';
import MessageActions from 'modules/shared/libs/chat/components/MessageActions';
import ToolCallIndicator from 'modules/shared/libs/chat/components/ToolCallIndicator';
import {
  getCitationTypeIcon,
  getEffectiveCitationTitle,
  parseIndexerUrl,
} from 'modules/shared/libs/chat/utils/sources.utils';

import { ActionButton, CardActions } from './answer-chat-actions';

import type { AnswerFollowupAction } from '../../utils/message-conversion.utils';
import type {
  MessageActionsContext,
  MessageRenderContext,
} from 'modules/shared/libs/chat/components/ChatStream';
import type {
  ChatStatus,
  ChatUIMessage,
  CitationSource,
} from 'modules/shared/libs/chat/types';
import type { ToolFriendlyNamesMap } from 'modules/shared/libs/chat/utils/tool-calling-utils';
import type { ReactNode } from 'react';

type FollowupsPart = {
  type: 'data-followups';
  data: AnswerFollowupAction[];
};

type AnswerChatProps = {
  messages: ChatUIMessage[];
  status: ChatStatus;
  error?: Error;
  regenerate: () => void;
  stop: () => void;
  sessionId: string;
  isLoadingHistory: boolean;
  headerHeight?: number;
  bodyPadding?: number;
  onCreateCard?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  // Add-to-Digest — independent of Create Card. Materializes a digest item
  // directly from the answer (no card intermediary), so visible whether or
  // not a card has been linked to the answer. Parent gates on curator
  // access before providing this handler.
  onAddToDigest?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  linkedCardIds?: Record<string, number>;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
  onExport?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  toolFriendlyNames?: ToolFriendlyNamesMap;
  onFollowupClick?: (followup: AnswerFollowupAction) => void;
};

function AnswerMessageActions({
  context,
  onCreateCard,
  onAddToDigest,
  linkedCardId,
  onViewCard,
  onScheduleCard,
  onExport,
}: {
  context: MessageActionsContext;
  onCreateCard?: AnswerChatProps['onCreateCard'];
  onAddToDigest?: AnswerChatProps['onAddToDigest'];
  linkedCardId?: number;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
  onExport?: AnswerChatProps['onExport'];
}): ReactNode {
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const hasSources = context.sources.length > 0;

  const citationItems = useMemo(() => {
    return context.sources.map((source) => {
      const parsedUrl = parseIndexerUrl(source.url);
      const IconComponent = getCitationTypeIcon(parsedUrl?.type || 'url');
      const effectiveTitle = getEffectiveCitationTitle(source);
      const snippetId =
        parsedUrl?.indexerType === 'curated-snippets'
          ? parsedUrl?.id || null
          : null;

      return {
        key: source.url,
        source,
        icon: IconComponent,
        title: effectiveTitle,
        snippetId,
      };
    });
  }, [context.sources]);

  const handleToggleSources = useCallback(() => {
    setSourcesExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <MessageActions isHovered={context.isHovered}>
        {hasSources && (
          <ActionButton
            icon={sourcesExpanded ? CaretUpIcon : CaretDownIcon}
            label={`Sources (${context.sources.length})`}
            onClick={handleToggleSources}
          />
        )}
        <CopyMessageAction
          messageText={context.messageText}
          sources={context.sources}
          sessionId={context.sessionId}
          messageId={context.messageId}
          getRenderedHtml={context.getRenderedHtml}
        />
        <CardActions
          messageId={context.messageId}
          messageText={context.messageText}
          sources={context.sources}
          userQuestion={context.userQuestion}
          onCreateCard={onCreateCard}
          onAddToDigest={onAddToDigest}
          linkedCardId={linkedCardId}
          onViewCard={onViewCard}
          onScheduleCard={onScheduleCard}
        />
        {onExport && (
          <ActionButton
            icon={ExportIcon}
            label="Export"
            onClick={() =>
              onExport(
                context.messageId,
                context.messageText,
                context.sources,
                context.userQuestion,
              )
            }
          />
        )}
      </MessageActions>

      {sourcesExpanded && hasSources && (
        <Stack gap="xs" bg="gray.0" p="md" bdrs="lg">
          {citationItems.map((item, index) => (
            <CitationItem
              key={item.key}
              source={item.source}
              icon={item.icon}
              title={item.title}
              snippetId={item.snippetId}
              number={index + 1}
            />
          ))}
        </Stack>
      )}
    </>
  );
}

export function AnswerChat({
  messages,
  status,
  error,
  regenerate,
  stop,
  sessionId,
  isLoadingHistory,
  headerHeight = 80,
  bodyPadding = 16,
  onCreateCard,
  onAddToDigest,
  linkedCardIds,
  onViewCard,
  onScheduleCard,
  onExport,
  toolFriendlyNames,
  onFollowupClick,
}: AnswerChatProps): ReactNode {
  const { t } = useTranslation(['Dashboard']);

  const { ref: followupRef, width: followupWidth } = useElementSize();

  const renderMessageFooter = useCallback(
    (
      message: ChatUIMessage,
      context: MessageRenderContext,
    ): ReactNode | null => {
      if (message.role !== 'assistant') return null;
      // Only render followups under the current (latest) assistant message —
      // otherwise restored multi-turn threads accumulate stale buttons under
      // older answers. Mirrors Hatch's behavior in shared ChatStream.
      if (!context.isCurrentMessage) return null;

      const followupsPart = message.parts?.find(
        (part): part is FollowupsPart => part.type === 'data-followups',
      );

      const followups = followupsPart?.data ?? [];

      if (followups.length === 0 || !onFollowupClick) return null;

      return (
        <Group ref={followupRef} gap="xs" mt="md">
          {followups.map((followup, index) => (
            <Button
              className="preserve-styles"
              key={followup.action}
              variant={index === 0 ? 'filled' : 'light'}
              size={followupWidth && followupWidth < 400 ? 'xs' : 'sm'}
              onClick={() => onFollowupClick(followup)}
            >
              <Text inherit truncate>
                {followup.action}
              </Text>
            </Button>
          ))}
        </Group>
      );
    },
    [onFollowupClick, followupRef, followupWidth],
  );

  const renderMessageActions = useCallback(
    (context: MessageActionsContext): ReactNode | null => (
      <AnswerMessageActions
        context={context}
        onCreateCard={onCreateCard}
        onAddToDigest={onAddToDigest}
        linkedCardId={linkedCardIds?.[context.messageId]}
        onViewCard={onViewCard}
        onScheduleCard={onScheduleCard}
        onExport={onExport}
      />
    ),
    [
      onCreateCard,
      onAddToDigest,
      linkedCardIds,
      onViewCard,
      onScheduleCard,
      onExport,
    ],
  );

  const renderIndicators = useCallback(
    (context: {
      message: ChatUIMessage;
      status?: ChatStatus;
      isCurrentMessage?: boolean;
    }): ReactNode | null => (
      <ToolCallIndicator
        message={context.message}
        status={context.status}
        isCurrentMessage={context.isCurrentMessage}
        toolFriendlyNames={toolFriendlyNames}
      />
    ),
    [toolFriendlyNames],
  );

  if (isLoadingHistory) {
    return (
      <Box align="center" justify="center" fill>
        <Spinner />
      </Box>
    );
  }

  return (
    <Paper p="xl" withBorder>
      <Chat.Stream
        messages={messages}
        status={status}
        onStop={stop}
        error={error}
        regenerate={regenerate}
        sessionId={sessionId}
        headerHeight={headerHeight}
        bodyPadding={bodyPadding}
        emptyStateMessage={t('Dashboard:askKlue.placeholderPage')}
        useViewportMinHeight={false}
        renderMessageActions={renderMessageActions}
        renderMessageFooter={renderMessageFooter}
        renderIndicators={renderIndicators}
      />
    </Paper>
  );
}
